
(() => {
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const kgLabel = (min, max) => {
    const n = (v) => String(Number(v.toFixed(3)));
    return `${n(min)}-${n(max)} kg`;
  };

  class DeliveryCalculator extends HTMLElement {
    async connectedCallback() {
      this.endpoint = (this.dataset.endpoint || "").replace(/\/+$/, "");
      this.select = this.querySelector("[data-dc-country]");
      this.result = this.querySelector("[data-dc-result]");
      if (!this.endpoint || !this.select) return;

      this.select.addEventListener("change", () => this.calc());
      await this.loadCountries();
      if (this.select.value) this.calc();
    }

    say(html) { if (this.result) this.result.innerHTML = html; }

    async loadCountries() {
      const locale = document.documentElement.lang || "en";
      this.select.disabled = true;
      try {
        const res = await fetch(`${this.endpoint}/public/countries?locale=${encodeURIComponent(locale)}`);
        const data = await res.json();
        this.currency = data.currency;
        this.countries = data.countries;
        this.select.innerHTML =
          `<option value="">${esc(this.select.dataset.placeholder || "Country")}</option>` +
          data.countries.map((c) => `<option value="${c.iso}">${esc(c.name)}</option>`).join("");
        this.select.disabled = false;

        const want = (this.dataset.defaultCountry || "").toUpperCase();
        if (want && data.countries.some((c) => c.iso === want)) this.select.value = want;
        this.buildPicker();
      } catch (e) {
        console.error("[delivery-calculator]", e);
        this.say(`<p class="dcHint dcHint--error">${window.themeStrings?.cartError || "Error"}</p>`);
      }
    }

    buildPicker() {
      const wrap = this.querySelector(".select2Wrap");
      if (!wrap || this.picker) return;

      const flag = (iso) => `<span class="flag" data-code="${iso.toLowerCase()}"></span>`;
      const box = document.createElement("div");
      box.className = "dcCountry";
      box.innerHTML =
        `<button type="button" class="dcCountry__button" aria-haspopup="listbox" aria-expanded="false">
           <span class="flagWrap"></span>
           <span class="dcCountry__arrow" aria-hidden="true"></span>
         </button>
         <div class="dcCountry__panel" hidden>
           <input type="search" class="dcCountry__search" autocomplete="off"
                  placeholder="${esc(this.select.dataset.search || "Search")}">
           <ul class="dcCountry__list" role="listbox">${
             this.countries.map((c) =>
               `<li class="dcCountry__option" role="option" data-iso="${c.iso}" tabindex="-1">
                  <span class="flagWrap">${flag(c.iso)}<span class="text">${esc(c.name)}</span></span>
                </li>`).join("")
           }</ul>
           <p class="dcCountry__empty" hidden>${esc(this.select.dataset.notfound || "No results found")}</p>
         </div>`;
      wrap.appendChild(box);
      this.picker = box;

      const button = box.querySelector(".dcCountry__button");
      const panel = box.querySelector(".dcCountry__panel");
      const search = box.querySelector(".dcCountry__search");
      const empty = box.querySelector(".dcCountry__empty");
      const options = [...box.querySelectorAll(".dcCountry__option")];

      const paint = () => {
        const iso = this.select.value;
        const name = this.countries.find((c) => c.iso === iso)?.name;
        button.querySelector(".flagWrap").innerHTML = iso
          ? `${flag(iso)}<span class="text">${esc(name)}</span>`
          : `<span class="text">${esc(this.select.dataset.placeholder || "Country")}</span>`;
        options.forEach((o) => o.setAttribute("aria-selected", String(o.dataset.iso === iso)));
      };

      const open = (state) => {
        panel.hidden = !state;
        button.setAttribute("aria-expanded", String(state));
        box.classList.toggle("is-open", state);
        if (state) { search.value = ""; filter(""); search.focus(); }
      };

      const filter = (q) => {
        const needle = q.trim().toLowerCase();
        let shown = 0;
        options.forEach((o) => {
          const hit = !needle || o.textContent.toLowerCase().includes(needle)
                   || o.dataset.iso.toLowerCase().startsWith(needle);
          o.hidden = !hit;
          if (hit) shown += 1;
        });
        empty.hidden = shown > 0;
      };

      button.addEventListener("click", () => open(panel.hidden));
      search.addEventListener("input", () => filter(search.value));
      box.addEventListener("click", (e) => {
        const opt = e.target.closest(".dcCountry__option");
        if (!opt) return;
        this.select.value = opt.dataset.iso;
        this.select.dispatchEvent(new Event("change", { bubbles: true }));
        paint();
        open(false);
        button.focus();
      });

      box.addEventListener("keydown", (e) => { if (e.key === "Escape") { open(false); button.focus(); } });
      document.addEventListener("click", (e) => { if (!box.contains(e.target)) open(false); });

      paint();
    }

    head() {
      const c = (cls, text) => `<div class="col ${cls}"><div class="text">${esc(text)}</div></div>`;
      return `<div class="row head">${
        c("shipping", this.dataset.colCarrier || "Shipping")
      }${c("weight", this.dataset.colWeight || "Weight")
      }${c("price", this.dataset.colPrice || "Price")
      }${c("price", this.dataset.colTerms || "Delivery terms")}</div>`;
    }

    body(rates, money) {
      const cell = (cls, label, value) =>
        `<div class="col ${cls}"><div class="text">${esc(label)}</div><div class="value">${value}</div></div>`;
      const L = {
        w: this.dataset.colWeight || "Weight",
        p: this.dataset.colPrice || "Price",
        t: this.dataset.colTerms || "Delivery terms",
      };
      return `<div class="tableTbodyWrap">${rates.map((r) =>
        `<div class="row">
           <div class="col shipping"><div class="name">${esc(r.carrier)}</div></div>
           ${cell("weight", L.w, esc(kgLabel(r.min, r.max)))}
           ${cell("price", L.p, money(r.price))}
           ${cell("price", L.t, r.days ? esc(r.days) : "—")}
         </div>`).join("")}</div>`;
    }

    async calc() {
      const iso = this.select.value;
      if (!iso) { this.say(`<p class="dcHint">${esc(this.select.dataset.placeholder || "Choose a country")}</p>`); return; }

      this.say(`<p class="dcHint">${window.themeStrings?.dcLoading || "…"}</p>`);
      try {

        const res = await fetch(`${this.endpoint}/public/rates?iso=${encodeURIComponent(iso)}`);
        const data = await res.json();
        if (!data.rates?.length) {
          this.say(`<p class="dcHint">${window.themeStrings?.dcNone || "No delivery options"}</p>`);
          return;
        }
        const fmt = (val, symbol) =>
          `${Number.isInteger(val) ? String(val) : val.toFixed(2).replace(".", ",")}\u00a0${symbol}`;
        const active = window.themeStrings?.currency || window.Shopify?.currency?.active || "EUR";
        const rate = Number(window.Shopify?.currency?.rate) || 1;
        const base = data.currency || "EUR";
        const money = (v) => {
          if (iso === "PL" && active === "PLN" && base !== "PLN") {
            return fmt(Math.round(v * rate * 100) / 100, window.themeStrings?.currencySymbol || "zł");
          }
          return fmt(Number(v), "\u20ac");
        };
        this.say(this.head() + this.body(data.rates, money));
      } catch (e) {
        console.error("[delivery-calculator]", e);
        this.say(`<p class="dcHint dcHint--error">${window.themeStrings?.cartError || "Error"}</p>`);
      }
    }
  }

  if (!customElements.get("delivery-calculator")) {
    customElements.define("delivery-calculator", DeliveryCalculator);
  }
})();
